import { AttachmentMeta } from "@/indexedDB/types";
import { decryptMessageAttachment, normalizeMimeType } from "./crypto";

import { isUserAuthenticated } from "./auth";
import { getOrDecapsulateCSK } from "./cryptoCache";

import { processAttachmentBucketUrl } from "@/lib/utils";
import { requestBlob } from "@/server/apiService";
import { isBlobReadable } from "@/lib/storage/blobLiveness";
import { useDownloadProgressState } from "@/zustand/DownloadProgressState";
import { PDF_PREVIEW_MAX_BYTES } from "@/config/constants";
import { runDeduplicated } from "./dedupeInFlight";
import {
  deleteCachedMedia,
  getCachedMedia,
  mediaCacheKey,
  setCachedMedia,
} from "./mediaMemoryCache";

import { getChatFromLocalDB } from "@/indexedDB/services/chats";
import {
  decapsulateSecurePayload,
  decryptPrivateKey,
} from "@/lib/crypto/crypto";

import {
  addFileAttachmentToLocalDB,
  addMediaAttachmentToLocalDB,
  addVoiceAttachmentToLocalDB,
  getMediaAttachmentFromLocalDB,
  getVoiceAttachmentFromLocalDB,
  getFileAttachmentContentFromLocalDB,
  getMediaAttachmentContentFromLocalDB,
  updateMediaAttachmentContent,
} from "@/indexedDB/services/attachments";

// AES-GCM framing on a whole-file attachment: 12-byte nonce + 16-byte auth tag
// (see windowCryptoEncrypt) — the encrypted object is exactly this much larger
// than the plaintext, letting progress be determinate even without a
// Content-Length header.
const ENCRYPTED_OVERHEAD_BYTES = 28;

// Ownership token for the id-keyed download-progress entry. dedupeInFlight
// deliberately lets an aborted run keep unwinding while a FRESH run for the
// same key starts — both write the same bare-attachmentId entry, so a dying
// run's cleanup must only ever remove what IT published, never the fresh
// run's live ring.
let nextProgressOwner = 0;

type ProcessMediaAttachmentOptions = {
  chatId: string;
  messageId: string;
  messageTimestamp: string;
  attachmentMeta: AttachmentMeta;
  getThumbnail: boolean;
  signal?: AbortSignal;
};

function normalizeToBlob(content: Blob | File): Blob {
  return content as Blob;
}

export const processMediaAttachment = async ({
  chatId,
  messageId,
  messageTimestamp,
  attachmentMeta,
  getThumbnail,
  signal,
}: ProcessMediaAttachmentOptions): Promise<Blob | null> => {
  // extra check on abort controller
  if (signal?.aborted) {
    return null;
  }

  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    return null;
  }

  const field = getThumbnail ? "thumbnail" : "content";
  // Synchronously-readable mirror of what we've already decrypted this session,
  // so a re-open/swipe-back can paint the real media on the first render instead
  // of a placeholder. See mediaMemoryCache.
  const memoryKey = mediaCacheKey(attachmentMeta.attachmentId, field);
  const fromMemory = getCachedMedia(memoryKey);
  if (fromMemory) {
    // Liveness guard: cached blobs can be FILE-BACKED handles whose backing
    // storage died after caching (a row rewrite, an eviction, an OPFS
    // delete). Serving one renders a broken image / a dead-URL video that
    // never recovers. A dead handle degrades to a cache miss instead — the
    // durable tier or the network self-heals it. 1-byte read; negligible.
    if (await isBlobReadable(fromMemory)) {
      return fromMemory;
    }
    deleteCachedMedia(memoryKey);
  }

  // De-duplicate concurrent requests for the same attachment + kind: overlapping
  // callers (the open viewer, its ±1 prefetch, auto-download) share ONE
  // download + decrypt instead of each doing the full, expensive work. The
  // shared run is cancelled only once every caller has aborted (runDeduplicated).
  return runDeduplicated(
    `media:${attachmentMeta.attachmentId}:${field}`,
    signal,
    async (opSignal) => {
      const progressOwner = ++nextProgressOwner;
      try {
        // Check if attachment is already in local db (may be in OPFS or IndexedDB)
        const cachedBlob = await getMediaAttachmentContentFromLocalDB(
          attachmentMeta.attachmentId,
          field,
        );
        // Same liveness guard as the memory tier above: an IDB/OPFS handle
        // whose backing died must fall through to a network re-fetch, not be
        // served (and not poison the memory cache).
        if (cachedBlob && (await isBlobReadable(cachedBlob))) {
          // Re-wrap cached blobs that were stored before MIME types were preserved
          if (!cachedBlob.type) {
            const expectedMime = getThumbnail
              ? "image/webp"
              : normalizeMimeType(attachmentMeta.fileType);
            if (expectedMime) {
              const retyped = cachedBlob.slice(
                0,
                cachedBlob.size,
                expectedMime,
              );
              setCachedMedia(memoryKey, retyped);
              return retyped;
            }
          }
          setCachedMedia(memoryKey, cachedBlob);
          return cachedBlob;
        }

        if (opSignal.aborted) {
          return null;
        }

        // Get existing entity to check if we need to update or create
        const existingEntity = await getMediaAttachmentFromLocalDB(
          attachmentMeta.attachmentId,
        );

        const id = getThumbnail
          ? attachmentMeta.attachmentThumbnailId
          : attachmentMeta.attachmentId;

        if (!id) {
          return null;
        }

        const attachmentUrl = processAttachmentBucketUrl(id);

        // Publish byte progress for the FULL-CONTENT download (the viewer's
        // ring) into the id-keyed store — never a per-caller callback, which
        // the single-flight dedupe would silently drop for every caller but
        // the first. Total: the transport's Content-Length when present, else
        // computed exactly from the meta (AES-GCM adds a 12-byte nonce + a
        // 16-byte tag to the plaintext size). Unknown total → no entries (the
        // ring simply doesn't render). Capped at 95% — decrypt still follows.
        const trackProgress = !getThumbnail;
        const fallbackTotal =
          attachmentMeta.fileSize > 0
            ? attachmentMeta.fileSize + ENCRYPTED_OVERHEAD_BYTES
            : 0;
        const eBlob = await requestBlob(
          attachmentUrl,
          opSignal,
          trackProgress
            ? (e) => {
                const total = e.total || fallbackTotal;
                if (total > 0) {
                  useDownloadProgressState
                    .getState()
                    .setProgress(
                      attachmentMeta.attachmentId,
                      Math.min(95, (e.loaded / total) * 100),
                      progressOwner,
                    );
                }
              }
            : undefined,
        );

        if (!eBlob || eBlob.size === 0) {
          return null;
        }

        // Bail before the expensive decrypt if everyone has since navigated away.
        if (opSignal.aborted) {
          return null;
        }

        const localChatData = await getChatFromLocalDB(chatId);

        if (!localChatData) {
          return null;
        }

        const csk = await getOrDecapsulateCSK({
          username: userFingerPrint.username,
          dk: userFingerPrint.dk,
          ePrK: userFingerPrint.ePrK,
          eCSK: localChatData.eCSK,
        });

        // Thumbnails are always WebP (produced by createThumbnail via canvas.toBlob)
        const mimeType = getThumbnail ? "image/webp" : attachmentMeta.fileType;
        const result = await decryptMessageAttachment(csk, eBlob, mimeType);

        if (!result) {
          return null;
        }

        // Persisting is a CACHE optimisation, not part of delivering the media.
        // Safari Private Browsing (and quota pressure / restricted storage)
        // makes IndexedDB and OPFS writes THROW — and because this write was
        // awaited inside the main try, one failure discarded a perfectly
        // decrypted blob and returned null, leaving the bubble stuck on its
        // blurred placeholder forever (every retry failing identically). A
        // failed cache write must never fail the read.
        try {
          if (existingEntity) {
            await updateMediaAttachmentContent(
              attachmentMeta.attachmentId,
              field,
              normalizeToBlob(result),
            );
          } else {
            await addMediaAttachmentToLocalDB({
              id: attachmentMeta.attachmentId,
              chatId,
              messageId,
              timestamp: messageTimestamp,
              [field]: normalizeToBlob(result),
              fileType: attachmentMeta?.fileType,
              filename: attachmentMeta?.filename,
            });
          }
        } catch {
          // No durable cache available — the in-memory cache below still
          // serves this session.
        }

        setCachedMedia(memoryKey, normalizeToBlob(result));
        return result;
      } catch {
        return null;
      } finally {
        // The ring must never outlive the run (abort/failure included) — but
        // only THIS run's entry: a fresh run may already own the key.
        if (!getThumbnail) {
          useDownloadProgressState
            .getState()
            .clearProgress(attachmentMeta.attachmentId, progressOwner);
        }
      }
    },
  );
};

type ProcessFileAttachmentOptions = {
  chatId: string;
  messageId: string;
  timestamp: string;
  attachmentMeta: AttachmentMeta;
  signal?: AbortSignal;
};
export const processFileAttachment = async ({
  chatId,
  messageId,
  timestamp,
  attachmentMeta,
  signal,
}: ProcessFileAttachmentOptions): Promise<Blob | null> => {
  // extra check on abort controller
  if (signal?.aborted) {
    return null;
  }

  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    return null;
  }

  // Dedupe concurrent requests for the same file so overlapping callers share
  // one download + decrypt; abort is cooperative (see runDeduplicated).
  return runDeduplicated(
    `file:${attachmentMeta.attachmentId}`,
    signal,
    async (opSignal) => {
      const progressOwner = ++nextProgressOwner;
      try {
        // Check if attachment is already in local db (may be in OPFS or IndexedDB)
        const cachedBlob = await getFileAttachmentContentFromLocalDB(
          attachmentMeta.attachmentId,
        );

        // Liveness guard (see processMediaAttachment): a dead handle degrades
        // to a re-download, never a broken document.
        if (cachedBlob && (await isBlobReadable(cachedBlob))) {
          // Re-wrap cached blobs that were stored before MIME types were preserved
          if (!cachedBlob.type && attachmentMeta.fileType) {
            const validMime = normalizeMimeType(attachmentMeta.fileType);
            if (validMime) {
              return cachedBlob.slice(0, cachedBlob.size, validMime);
            }
          }
          return cachedBlob;
        }

        if (opSignal.aborted) {
          return null;
        }

        const attachmentUrl = processAttachmentBucketUrl(
          attachmentMeta.attachmentId,
        );

        // Byte progress for the document download, published into the same
        // id-keyed store the media ring uses (never a per-caller callback —
        // runDeduplicated executes only the first caller's closure, so a later
        // joiner's callback would be silently dropped). Total is the
        // transport's Content-Length when present, else computed exactly from
        // the meta. Capped at 95% because the decrypt still follows.
        const fallbackTotal =
          attachmentMeta.fileSize > 0
            ? attachmentMeta.fileSize + ENCRYPTED_OVERHEAD_BYTES
            : 0;
        const eBlob = await requestBlob(attachmentUrl, opSignal, (e) => {
          const total = e.total || fallbackTotal;
          if (total > 0) {
            useDownloadProgressState
              .getState()
              .setProgress(
                attachmentMeta.attachmentId,
                Math.min(95, (e.loaded / total) * 100),
                progressOwner,
              );
          }
        });

        if (!eBlob || eBlob.size === 0) {
          return null;
        }

        // Bail before the expensive decrypt if everyone has since navigated away.
        if (opSignal.aborted) {
          return null;
        }

        const localChatData = await getChatFromLocalDB(chatId);

        if (!localChatData) {
          return null;
        }

        const csk = await getOrDecapsulateCSK({
          username: userFingerPrint.username,
          dk: userFingerPrint.dk,
          ePrK: userFingerPrint.ePrK,
          eCSK: localChatData.eCSK,
        });

        const result = await decryptMessageAttachment(
          csk,
          eBlob,
          attachmentMeta.fileType,
        );

        if (result) {
          // Don't cache what can only ever be saved to disk. Above the preview
          // ceiling a document never opens in the viewer, so a local copy would
          // only speed up a REPEAT download — while costing its full size in
          // OPFS on top of the copy the user already has on their device, and
          // pushing the quota sweep to evict their other attachments to make
          // room. Metadata is not lost: the message itself carries filename,
          // size and type (the Files tab reads messages, not this table).
          if (attachmentMeta.fileSize <= PDF_PREVIEW_MAX_BYTES) {
            // Best-effort cache write — never fail the read (see the media path).
            try {
              await addFileAttachmentToLocalDB({
                id: attachmentMeta.attachmentId,
                chatId,
                messageId,
                timestamp,
                content: result,
                fileType: attachmentMeta?.fileType,
                filename: attachmentMeta.filename,
                fileSize: attachmentMeta.fileSize,
              });
            } catch {
              // Storage unavailable (private browsing / quota) — still deliver it.
            }
          }

          return result;
        }

        return null;
      } catch {
        return null;
      } finally {
        // The ring must never outlive the run (abort/failure included) — but
        // only THIS run's entry: a fresh run may already own the key.
        useDownloadProgressState
          .getState()
          .clearProgress(attachmentMeta.attachmentId, progressOwner);
      }
    },
  );
};

export const processVoiceAttachment = async ({
  chatId,
  messageId,
  timestamp,
  attachmentMeta,
  signal,
}: ProcessFileAttachmentOptions): Promise<Blob | null> => {
  // extra check on abort controller
  if (signal?.aborted) {
    return null;
  }

  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    return null;
  }

  try {
    // check if attachment is already in local db
    const blobFromLocalDb = await getVoiceAttachmentFromLocalDB(
      attachmentMeta.attachmentId,
    );

    if (blobFromLocalDb?.content) {
      // Re-wrap cached blobs that were stored before MIME types were preserved,
      // so iOS Safari can decode them without content-sniffing.
      if (!blobFromLocalDb.content.type && attachmentMeta.fileType) {
        const validMime = normalizeMimeType(attachmentMeta.fileType);
        if (validMime) {
          return blobFromLocalDb.content.slice(
            0,
            blobFromLocalDb.content.size,
            validMime,
          );
        }
      }
      return blobFromLocalDb.content;
    }

    const attachmentUrl = processAttachmentBucketUrl(
      attachmentMeta.attachmentId,
    );

    const eBlob = await requestBlob(attachmentUrl);

    if (!eBlob || eBlob.size === 0) {
      return null;
    }

    // get user`s encrypted private keys and password
    const userEncryptedPrivateKeys = userFingerPrint.ePrK;
    const userEncrypetdPassword = userFingerPrint.dk;

    // decrypt user`s private keys
    const userPK = await decryptPrivateKey(
      userEncryptedPrivateKeys,
      userEncrypetdPassword,
    );
    // TO DO => TTL cached csk s
    const localChatData = await getChatFromLocalDB(chatId);

    if (!localChatData) {
      return null;
    }

    // decaspulate chat secret key
    const csk = decapsulateSecurePayload({
      privateKeys: userPK,
      securePayload: localChatData.eCSK,
    });

    const result = await decryptMessageAttachment(
      csk,
      eBlob,
      attachmentMeta.fileType,
    );

    if (result) {
      // Best-effort cache write — never fail the read (see the media path).
      try {
        await addVoiceAttachmentToLocalDB({
          id: attachmentMeta.attachmentId,
          chatId,
          messageId,
          timestamp,
          content: result,
          fileType: attachmentMeta?.fileType,
          filename: attachmentMeta.filename,
          // fileSize: attachmentMeta.fileSize,
        });
      } catch {
        // Storage unavailable (private browsing / quota) — still deliver it.
      }

      return result;
    }

    return null;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return null;
  }
};
